import type { Section } from '../components/icons';

/**
 * What each Explore section says about itself, one line and one picture at a
 * time.
 *
 * The lines answer the question a visitor actually arrives with — "what is in
 * here?" — with specifics rather than a category list: somebody landing on
 * Services learns more from "a fundi for the leaking tap" than from the word
 * Maintenance.
 *
 * EVERY IMAGE IS USED ONCE. An earlier version repeated two photographs across
 * four slides, which reads as a slideshow that has run out rather than as a
 * section with something to show — the dots promise four things and deliver
 * two. Where there were not enough good pictures for four slides, the section
 * has two. Two good ones beat four with two bad.
 *
 * THE REGISTER IS ASPIRATIONAL, and that is a decision rather than an
 * oversight. The first set was shot documentary-style — a workshop with bare
 * blockwork, a bare room with a second-hand cooker in it — which was honest
 * about some of the market and wrong for a shop window: it made the product
 * look like it was for people with no other options. What is here now is the
 * register an estate agent would use, across price points, from a modern
 * walk-up to a penthouse.
 *
 * Homes deliberately mixes those price points. A section showing only glass
 * towers tells a bedsitter hunter they are in the wrong place; only walk-ups
 * tells the Westlands landlord the same.
 */

export interface Slide {
  /** One line, read in about three seconds. No full stop needed. */
  line: string;
  /** A file in web/public/explore. Absent is fine: the band is the section's colour. */
  image?: string;
}

export const EXPLORE_SLIDES: Record<Section, Slide[]> = {
  homes: [
    { line: 'Bedsitters to family houses — listed by the landlords who manage them', image: '/explore/premium-1.webp' },
    { line: 'Rent, deposit and what is included, stated before you call', image: '/explore/homes-1.webp' },
    { line: 'From a walk-up in Kasarani to a penthouse in Westlands', image: '/explore/premium-2.webp' },
    { line: 'Filter by county, bedrooms and what you can actually afford', image: '/explore/homes-2.webp' },
    { line: 'Parking, security, water and what the building is actually like', image: '/explore/premium-3.webp' },
    { line: 'Taken listings stay up and say so, instead of wasting your afternoon', image: '/explore/homes-4.webp' },
  ],
  /*
   * Buy is shot at the top of the market on purpose. The people who list here
   * are selling penthouses and new-build towers, and a section led by a modest
   * walk-up tells them at a glance that it is not for their property — the
   * same mistake Homes made in its first set, in the other direction. Homes
   * still mixes price points, because a bedsitter hunter has to see themselves
   * in it; somebody with 145 million to spend does not.
   */
  buy: [
    { line: 'Houses, apartments and plots for sale across Kenya', image: '/explore/buy-1.webp' },
    { line: 'The asking price, the tenure and the title status, stated up front', image: '/explore/buy-2.webp' },
    { line: 'Penthouses, whole floors and new-build towers', image: '/explore/buy-3.webp' },
    { line: 'Every property is read by a person before it appears here', image: '/explore/buy-4.webp' },
  ],
  services: [
    { line: 'A fundi for the leaking tap, an electrician for the socket that sparks', image: '/explore/services-1.webp' },
    { line: 'Cleaners, movers, painters, carpenters and electricians near you', image: '/explore/services-2.webp' },
    { line: 'Every business is read by a person before it appears here', image: '/explore/services-3.webp' },
    { line: 'Numbers are public — ring them directly, no middleman, no fee', image: '/explore/services-4.webp' },
  ],
  market: [
    { line: 'Furniture and appliances from people moving house', image: '/explore/market-1.webp' },
    { line: 'Sofas, beds and dining sets, collected the same day in your county', image: '/explore/market-3.webp' },
    { line: 'Free to list, sixty days to sell, and sold things say so', image: '/explore/market-2.webp' },
    { line: 'Cookers, fridges and the small appliances that come with a move', image: '/explore/market-4.webp' },
  ],
  post: [
    { line: 'A business, a thing to sell, or a vacant unit — one door for all three', image: '/explore/post-3.webp' },
    { line: 'Free to post. Signing in takes a minute', image: '/explore/post-2.webp' },
    { line: 'A person reads every listing before it goes live', image: '/explore/post-1.webp' },
  ],
};
